// client/src/components/ofertas/RecorrenciaFields.js
//
// Campos de "Repetir a cada" (mesmo formato de server/models/subschemas/RecorrenciaSchema.js)
// pra configurar como as sessões de um pacote se repetem. Reusado dentro do
// card de cada pacote em OfertaForm.js (e no espelho mobile,
// mobile/components/ofertas/recorrencia-fields.tsx).
//
// Componente controlado — value/onChange no formato exato do subschema, pra
// bater 1:1 com o payload esperado pelo backend, sem nenhuma tradução no
// submit do formulário pai.
import React from 'react';
import PropTypes from 'prop-types';

const DIAS_SEMANA = [
    { value: 0, label: 'Dom' },
    { value: 1, label: 'Seg' },
    { value: 2, label: 'Ter' },
    { value: 3, label: 'Qua' },
    { value: 4, label: 'Qui' },
    { value: 5, label: 'Sex' },
    { value: 6, label: 'Sáb' },
];

const ORDINAIS = [
    { value: 1, label: '1ª' },
    { value: 2, label: '2ª' },
    { value: 3, label: '3ª' },
    { value: 4, label: '4ª' },
    { value: -1, label: 'Última' },
];

const PRESETS = [
    { value: 'nao_repete', label: 'Não se repete' },
    { value: 'diaria', label: 'Todos os dias' },
    { value: 'semanal', label: 'Semanalmente' },
    { value: 'mensal', label: 'Mensalmente' },
    { value: 'anual', label: 'Anualmente' },
    { value: 'dias_uteis', label: 'Todos os dias da semana (segunda a sexta)' },
    { value: 'personalizada', label: 'Personalizar...' },
];

const RECORRENCIA_PADRAO = {
    ativa: false,
    frequencia: 'semanal',
    intervalo: 1,
    diasDaSemana: [],
    clienteEscolheDia: false,
    tipoMensal: 'dia_do_mes',
    diaDoMes: undefined,
    ordinalSemana: undefined,
    diaDaSemanaOrdinal: undefined,
    mesDoAno: undefined,
    termino: { tipo: 'nunca' },
    presetSelecionado: 'nao_repete',
};

// Espelha RecorrenciaSchema.methods.aplicarPreset do backend — só preenche
// os campos concretos a partir do preset escolhido, usando hoje como
// referência (não existe "data de início" no cadastro do pacote em si, só
// no momento em que a série é de fato gerada no agendamento).
const aplicarPreset = (preset) => {
    const hoje = new Date();
    const base = { ...RECORRENCIA_PADRAO, presetSelecionado: preset };

    switch (preset) {
        case 'nao_repete':
            return { ...base, ativa: false };
        case 'diaria':
            return { ...base, ativa: true, frequencia: 'diaria', intervalo: 1 };
        case 'semanal':
            return { ...base, ativa: true, frequencia: 'semanal', intervalo: 1, diasDaSemana: [hoje.getDay()] };
        case 'mensal':
            return { ...base, ativa: true, frequencia: 'mensal', intervalo: 1, tipoMensal: 'dia_do_mes', diaDoMes: hoje.getDate() };
        case 'anual':
            return { ...base, ativa: true, frequencia: 'anual', intervalo: 1, mesDoAno: hoje.getMonth() + 1, diaDoMes: hoje.getDate() };
        case 'dias_uteis':
            return { ...base, ativa: true, frequencia: 'semanal', intervalo: 1, diasDaSemana: [1, 2, 3, 4, 5] };
        case 'personalizada':
            return { ...base, ativa: true, frequencia: 'semanal', intervalo: 1, diasDaSemana: [hoje.getDay()] };
        default:
            return base;
    }
};

const RecorrenciaFields = ({ value, onChange, idPrefix }) => {
    const recorrencia = value || RECORRENCIA_PADRAO;

    const handlePresetChange = (preset) => {
        onChange(aplicarPreset(preset));
    };

    const set = (patch) => onChange({ ...recorrencia, ...patch });

    const toggleDiaSemana = (dia) => {
        const atuais = recorrencia.diasDaSemana || [];
        const novos = atuais.includes(dia) ? atuais.filter((d) => d !== dia) : [...atuais, dia].sort();
        set({ diasDaSemana: novos });
    };

    return (
        <div className="form-group full-width" style={{ borderTop: '1px dashed var(--color-border, #e5e7eb)', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
            <label htmlFor={`${idPrefix}-preset`} className="text-xs">Repetir a cada</label>
            <select
                id={`${idPrefix}-preset`}
                className="form-input"
                value={recorrencia.presetSelecionado || 'nao_repete'}
                onChange={(e) => handlePresetChange(e.target.value)}
            >
                {PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>

            {recorrencia.ativa && (
                <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <div className="form-grid-container-cols-2">
                        <div className="form-group">
                            <label htmlFor={`${idPrefix}-frequencia`} className="text-xs">Frequência</label>
                            <select
                                id={`${idPrefix}-frequencia`}
                                className="form-input"
                                value={recorrencia.frequencia}
                                onChange={(e) => set({ frequencia: e.target.value, presetSelecionado: 'personalizada' })}
                            >
                                <option value="diaria">Diária</option>
                                <option value="semanal">Semanal</option>
                                <option value="mensal">Mensal</option>
                                <option value="anual">Anual</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor={`${idPrefix}-intervalo`} className="text-xs">A cada</label>
                            <input
                                id={`${idPrefix}-intervalo`}
                                type="number"
                                min="1"
                                className="form-input"
                                value={recorrencia.intervalo ?? 1}
                                onChange={(e) => set({ intervalo: Math.max(1, Number(e.target.value) || 1), presetSelecionado: 'personalizada' })}
                            />
                        </div>
                    </div>

                    {recorrencia.frequencia === 'semanal' && (
                        <div className="form-group full-width">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'normal' }}>
                                <input
                                    type="checkbox"
                                    checked={!!recorrencia.clienteEscolheDia}
                                    onChange={(e) => set({ clienteEscolheDia: e.target.checked, presetSelecionado: 'personalizada' })}
                                />
                                Cliente escolhe o dia (1 sessão por semana, sempre no mesmo dia da 1ª sessão)
                            </label>
                            <small className="form-help" style={{ display: 'block', marginBottom: '0.35rem' }}>
                                {recorrencia.clienteEscolheDia
                                    ? 'O cliente define o dia fixo ao agendar a 1ª sessão — as próximas repetem nesse mesmo dia, toda semana. Marque abaixo quais dias ele pode escolher.'
                                    : 'Desmarcado: os dias marcados abaixo repetem TODOS, toda semana (ex.: seg+qua+sex = 3 sessões por semana).'}
                            </small>
                            <label className="text-xs">{recorrencia.clienteEscolheDia ? 'Dias permitidos para o cliente escolher' : 'Dias da semana'}</label>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                                {DIAS_SEMANA.map((dia) => {
                                    const ativo = (recorrencia.diasDaSemana || []).includes(dia.value);
                                    return (
                                        <button
                                            key={dia.value}
                                            type="button"
                                            onClick={() => { toggleDiaSemana(dia.value); set({ presetSelecionado: 'personalizada' }); }}
                                            className={`action-button ${ativo ? 'action-button-primary' : 'action-button-secondary'}`}
                                            style={{ padding: '0.35rem 0.75rem' }}
                                        >
                                            {dia.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {recorrencia.frequencia === 'mensal' && (
                        <div className="form-grid-container-cols-2">
                            <div className="form-group">
                                <label htmlFor={`${idPrefix}-tipo-mensal`} className="text-xs">Repetir por</label>
                                <select
                                    id={`${idPrefix}-tipo-mensal`}
                                    className="form-input"
                                    value={recorrencia.tipoMensal}
                                    onChange={(e) => set({ tipoMensal: e.target.value, presetSelecionado: 'personalizada' })}
                                >
                                    <option value="dia_do_mes">Dia fixo do mês</option>
                                    <option value="dia_da_semana">Posição no mês (ex.: 2ª sexta-feira)</option>
                                </select>
                            </div>
                            {recorrencia.tipoMensal === 'dia_do_mes' ? (
                                <div className="form-group">
                                    <label htmlFor={`${idPrefix}-dia-mes`} className="text-xs">Dia do mês</label>
                                    <input
                                        id={`${idPrefix}-dia-mes`}
                                        type="number" min="1" max="31"
                                        className="form-input"
                                        value={recorrencia.diaDoMes ?? ''}
                                        onChange={(e) => set({ diaDoMes: Number(e.target.value) || undefined, presetSelecionado: 'personalizada' })}
                                    />
                                </div>
                            ) : (
                                <div className="form-group">
                                    <label className="text-xs">Posição + dia da semana</label>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <select
                                            className="form-input"
                                            value={recorrencia.ordinalSemana ?? ''}
                                            onChange={(e) => set({ ordinalSemana: Number(e.target.value), presetSelecionado: 'personalizada' })}
                                        >
                                            <option value="">Posição...</option>
                                            {ORDINAIS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </select>
                                        <select
                                            className="form-input"
                                            value={recorrencia.diaDaSemanaOrdinal ?? ''}
                                            onChange={(e) => set({ diaDaSemanaOrdinal: Number(e.target.value), presetSelecionado: 'personalizada' })}
                                        >
                                            <option value="">Dia...</option>
                                            {DIAS_SEMANA.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                                        </select>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {recorrencia.frequencia === 'anual' && (
                        <div className="form-grid-container-cols-2">
                            <div className="form-group">
                                <label htmlFor={`${idPrefix}-mes-ano`} className="text-xs">Mês</label>
                                <select
                                    id={`${idPrefix}-mes-ano`}
                                    className="form-input"
                                    value={recorrencia.mesDoAno ?? ''}
                                    onChange={(e) => set({ mesDoAno: Number(e.target.value) || undefined, presetSelecionado: 'personalizada' })}
                                >
                                    <option value="">Mês...</option>
                                    {['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'].map((m, i) => (
                                        <option key={m} value={i + 1}>{m}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label htmlFor={`${idPrefix}-dia-ano`} className="text-xs">Dia</label>
                                <input
                                    id={`${idPrefix}-dia-ano`}
                                    type="number" min="1" max="31"
                                    className="form-input"
                                    value={recorrencia.diaDoMes ?? ''}
                                    onChange={(e) => set({ diaDoMes: Number(e.target.value) || undefined, presetSelecionado: 'personalizada' })}
                                />
                            </div>
                        </div>
                    )}

                    <div className="form-group full-width">
                        <label className="text-xs">Termina</label>
                        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', marginTop: '0.25rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontWeight: 'normal' }}>
                                <input type="radio" checked={recorrencia.termino?.tipo === 'nunca'} onChange={() => set({ termino: { tipo: 'nunca' } })} />
                                Nunca
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontWeight: 'normal' }}>
                                <input type="radio" checked={recorrencia.termino?.tipo === 'em_data'} onChange={() => set({ termino: { tipo: 'em_data', data: recorrencia.termino?.data } })} />
                                Em
                                <input
                                    type="date"
                                    className="form-input"
                                    style={{ width: 'auto' }}
                                    disabled={recorrencia.termino?.tipo !== 'em_data'}
                                    value={recorrencia.termino?.data ? String(recorrencia.termino.data).slice(0, 10) : ''}
                                    onChange={(e) => set({ termino: { tipo: 'em_data', data: e.target.value } })}
                                />
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontWeight: 'normal' }}>
                                <input type="radio" checked={recorrencia.termino?.tipo === 'apos_ocorrencias'} onChange={() => set({ termino: { tipo: 'apos_ocorrencias', ocorrencias: recorrencia.termino?.ocorrencias || 10 } })} />
                                Após
                                <input
                                    type="number" min="1"
                                    className="form-input"
                                    style={{ width: '5rem' }}
                                    disabled={recorrencia.termino?.tipo !== 'apos_ocorrencias'}
                                    value={recorrencia.termino?.ocorrencias ?? ''}
                                    onChange={(e) => set({ termino: { tipo: 'apos_ocorrencias', ocorrencias: Number(e.target.value) || 1 } })}
                                />
                                ocorrências
                            </label>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

RecorrenciaFields.propTypes = {
    value: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    idPrefix: PropTypes.string,
};

RecorrenciaFields.defaultProps = {
    value: null,
    idPrefix: 'recorrencia',
};

export { RECORRENCIA_PADRAO };
export default RecorrenciaFields;
